// Utility functions for normalizing wine data between eating in and eating out flows

/**
 * Normalizes wine data to a unified structure regardless of source
 * @param {Object} wine - Raw wine data from API
 * @param {boolean} isEatingIn - Whether this is for the eating in flow
 * @returns {Object} Normalized wine data
 */
export const normalizeWineData = (wine, isEatingIn) => {
  // Generate consistent ID
  const id = wine.id || `wine_${wine.rank || 0}`;
  
  // Normalize title based on flow type
  const title = isEatingIn 
    ? wine.wine_style 
    : [wine.producer, wine.wine_name, wine.vintage]
        .filter(item => item && item !== 'NOT KNOWN' && item !== 'N/A')
        .join(' ');
  
  // Normalize subtitle
  const subtitle = isEatingIn ? wine.wine_type : null;
  
  // Normalize price information
  const price = !isEatingIn ? {
    bottle: wine.bottle_price && wine.bottle_price !== 'N/A' ? wine.bottle_price : null,
    glass: wine.glass_price && wine.glass_price !== 'N/A' ? wine.glass_price : null
  } : null;
  
  // Normalize wine details
  const details = {
    grapes: isEatingIn ? wine.grape_varieties : wine.grapes,
    region: wine.region,
    characteristics: wine.characteristics
  };
  
  // Normalize reason/pairing explanation
  const reason = isEatingIn ? wine.pairing_reason : wine.recommendation_reason;
  
  return {
    id,
    title: title || 'Unknown Wine',
    subtitle,
    price,
    details,
    reason: reason || 'No specific reason provided.',
    rank: wine.rank || 0,
    // Preserve original data for CSV logging and other purposes
    originalData: wine
  };
};

/**
 * Generates display strings for a normalized wine object
 * @param {Object} normalizedWine - Normalized wine data
 * @returns {Object} Display strings for the wine
 */
export const generateWineDisplayStrings = (normalizedWine) => {
  return {
    title: normalizedWine.title,
    subtitle: normalizedWine.subtitle,
    price: formatPriceDisplay(normalizedWine.price),
    grapes: normalizedWine.details.grapes || 'N/A',
    region: normalizedWine.details.region || 'N/A',
    characteristics: normalizedWine.details.characteristics || 'N/A',
    reason: normalizedWine.reason,
    rank: normalizedWine.rank
  };
};

/**
 * Formats price display for wine cards
 * @param {Object} price - Price object with bottle and glass properties
 * @returns {string} Formatted price string
 */
export const formatPriceDisplay = (price) => {
  if (!price) return '';
  
  const parts = [];
  if (price.bottle) parts.push(price.bottle);
  if (price.glass) parts.push(`${price.glass} glass`);
  
  return parts.join(' / ');
};

/**
 * Gets the appropriate header title based on flow and context
 * @param {boolean} isEatingIn - Whether this is eating in flow
 * @param {string} restaurant - Restaurant name (for eating out)
 * @returns {string} Header title
 */
export const getRecommendationTitle = (isEatingIn, restaurant = '') => {
  if (isEatingIn) {
    return 'Wine Recommendations for Your Home Cooking';
  }
  
  if (restaurant) {
    return `Recommended Wines at ${restaurant}`;
  }
  
  return 'Here are my wine recommendations for you:';
};

/**
 * Gets the appropriate intro message based on flow
 * @param {boolean} isEatingIn - Whether this is eating in flow
 * @returns {string} Intro message
 */
export const getIntroMessage = (isEatingIn) => {
  return isEatingIn 
    ? 'Here are wine styles that would pair beautifully with your cooking:'
    : 'Here are wines from the menu that would pair perfectly with your preferences:';
};

/**
 * Gets the appropriate loading message based on flow
 * @param {boolean} isEatingIn - Whether this is eating in flow
 * @returns {string} Loading message
 */
export const getLoadingMessage = (isEatingIn) => {
  return isEatingIn 
    ? 'Generating wine recommendations...'
    : 'I\'m carefully selecting the perfect wines for you...';
};
